<template>
	<Row class="page-container-row">
		<Col
			:span="24"
			:offset="0"
			:xl="{ span: 18, offset: 3 }"
			:xxl="{ span: 12, offset: 6 }"
		>
			<PageContainer>
				<VerticalLayout>
					<Spin :spinning="isLoading">
						<Card>
							<template #title>
								<BaselineFormHeader :baselineObj="baselineObj" :baselineDetails="baselineDetails" :messages="messages" />
							</template>
							<Form class="baseline-form" :model="formState" :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" :colon="false">
								<InfoInlineHint v-if="!isBrdcEnabled" formMode>
									<BaselineBrdcInfoInlineHintContent />
								</InfoInlineHint>
								<FormItem :label="$t('baselines-edit.fields.base')" v-bind="validateInfos.base">
									<BaseStationInBaselineStatus v-if="editMode && !isDeleted" :baseStation="baselineObj?.base ?? null" />
									<div v-else-if="editMode && isDeleted" class="pd-sm-x">{{ baselineDetails?.base?.mntpnt_int }}</div>
									<Select
										v-else
										class="ui-select"
										show-search
										v-model:value="formState.base"
										:options="bsOptions"
										:placeholder="$t('baselines-edit.messages.selectBaseStation')"
										:filter-option="filterOption"
									/>
								</FormItem>
								<FormItem :label="$t('baselines-edit.fields.rover')" v-bind="validateInfos.rover">
									<BaseStationInBaselineStatus v-if="editMode && !isDeleted" :baseStation="baselineObj?.rover ?? null" />
									<div v-else-if="editMode && isDeleted" class="pd-sm-x">{{ baselineDetails?.rover?.mntpnt_int }}</div>
									<Select
										v-else
										class="ui-select"
										show-search
										v-model:value="formState.rover"
										:options="bsOptions"
										:placeholder="$t('baselines-edit.messages.selectBaseStation')"
										:filter-option="filterOption"
									/>
								</FormItem>
								<FormItem :label="$t('baselines-edit.fields.description')" v-bind="validateInfos.description">
									<Textarea
										v-model:value="formState.description"
										:placeholder="$t('baselines-edit.fields.descriptionPlaceholder')"
										:disabled="isDeleted"
									/>
								</FormItem>
								<FormItem :label="$t('baselines-edit.fields.selectedGnss')" v-bind="validateInfos.selectedGnss">
									<CheckboxesComponent v-model:value="formState.selectedGnss" :disabled="baselineDetails.deleted" bordered>
										<Checkbox v-for="optionCode in selectedGnssOptions" :key="optionCode" :value="optionCode">
											{{ $t('common.gnssLabels.' + optionCode) }}
										</Checkbox>
									</CheckboxesComponent>
								</FormItem>
								<FormItem v-if="baselineId > 0" :label="$t('baselines-edit.fields.nmeaPortExt')">
									<span class="pd-sm-x test-nmea-port-ext">{{ formState.nmeaPortExt }}</span>
								</FormItem>
								<FormItem v-if="baselineId > 0" :label="$t('baselines-edit.fields.nmeaFtpPath')">
									<TypographyText code class="pd-sm-x test-nmea-ftp-path">{{ nmeaFtpPath }}</TypographyText>
								</FormItem>
								<FormItem v-if="baselineLength != null" :label="$t('baselines-edit.fields.baselineLength')">
									<span class="pd-sm-x test-baseline-length">{{ baselineLength }} {{ $t('baselines-edit.fields.meters') }}</span>
								</FormItem>
							</Form>
							<template #actions>
								<Space v-if="!isDeleted">
									<Button v-if="baselineObj != null" type="primary" @click.prevent="onSubmit(false)" :loading="isSubmitting" :disabled="!isBrdcEnabled">
										{{ messages.submitButtonLabel }}
									</Button>
									<Button type="primary" @click.prevent="onSubmit(true)" :loading="isSubmitting" :disabled="!isBrdcEnabled">
										{{ messages.submitAndCloseButtonLabel }}
									</Button>
								</Space>
								<ChangeStatusActionBar
									v-if="editMode"
									:target="baselineObj"
									:store="baselinesStore"
									mode="baseline"
									@delete="resetBaselineDetails"
									@restore="resetBaselineDetails"
								/>
							</template>
						</Card>
					</Spin>
					<BaselineDataDisplay
						v-if="editMode"
						:baselineId="baselineId"
						:isBaselineActive="isBaselineActive"
						:epochCounts="epochCounts"
						:qualityOptions="qualityOptions"
						v-model:selectedQuality="selectedQuality"
						:isStatsLoading="isStatsLoading"
						:allSeries="allSeries"
						:allOptions="allOptions"
					/>
				</VerticalLayout>
			</PageContainer>
		</Col>
	</Row>
	<RouteLeaveModal
		:visible="modalVisible"
		:confirm="modalOk"
		:cancel="modalCancel"
		:submit="onSubmitRouteLeaveModal"
		:loading="isSubmittingRouteLeaveModal"
		:submitText="messages.submitAndCloseButtonLabel"
	/>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue';

import {
	Form, FormItem, Button, Row, Col, Card, Space, Spin, Select, TypographyText, Textarea, Checkbox, message,
} from 'ant-design-vue';

import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import RouteLeaveModal from '@/components/common/RouteLeaveModal.vue';
import { useRouteLeaveModal } from '@/components/common/useRouteLeaveModal';

import Utils from '@/api/Utils';
import Validators from '@/api/Validators';

import PageContainer from '@/components/common/PageContainer.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';

import BaselineFormHeader from '@/components/addBaseline/BaselineFormHeader.vue';
import BaseStationInBaselineStatus from '@/components/addBaseline/BaseStationInBaselineStatus.vue';
import BaselineDataDisplay from '@/components/addBaseline/BaselineDataDisplay.vue';

import ChangeStatusActionBar from '@/components/baseStations/ChangeStatusActionBar.vue';

import InfoInlineHint from '@/components/common/InfoInlineHint.vue';
import BaselineBrdcInfoInlineHintContent from '@/components/common/BaselineBrdcInfoInlineHintContent.vue';
import CheckboxesComponent from '@/components/common/CheckboxesComponent.vue';

import { useBaselineData } from '@/components/addBaseline/useBaselineData';
import { useFormSubmit } from '@/components/addBaseline/useFormSubmit';
import { useMessages } from '@/components/addBaseStation/useMessages';

import { useBaselinesStore } from '@/stores/baselinesStore';

import { useBaselineChartsData } from '@/components/addBaseline/useBaselineChartsData';


export default {
	components: {
		Form, FormItem, Button, Row, Col, Card, Spin, Space, PageContainer, Select, TypographyText, Textarea,
		BaselineFormHeader, ChangeStatusActionBar, BaseStationInBaselineStatus, CheckboxesComponent,
		RouteLeaveModal,
		InfoInlineHint, BaselineBrdcInfoInlineHintContent,
		VerticalLayout, BaselineDataDisplay,
		Checkbox,
	},
	setup() {
		const { t } = useI18n();
		const baselineId = ref(0);
		const editMode = computed(() => baselineId.value > 0);
		const { messages, setEditModeMessages } = useMessages('add-baseline', 'baselines-edit');
		/*
		Надо использовать отдельно baselineObj и baselineDetails, так как baselineObj -- это computed,
		связанный со store. При изменении статуса БС, надо получить состояние из store.
		baselineDetails -- это просто dict с уточненными данными
		*/
		const {
			formState,
			baselineDetails,
			baselineObj,
			bsOptions,
			isLoading,
			loadBaselineDetails,
			isDeleted,
			nmeaFtpPath,
			isBaselineActive,
			selectedGnssOptions,
		} = useBaselineData(baselineId);
		const filterOption = (input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;

		const { formChangedStatuses, modalVisible, modalOk, modalCancel } = useRouteLeaveModal();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(formState, formChangedStatuses, 'addBaselineForm');
		const resetBaselineDetails = () => {
			resetChangedState();
			pauseWatch();
			loadBaselineDetails();
		}

		const { isStatsLoading, allSeries, allOptions, selectedQuality, qualityOptions, epochCounts, updateStats } = useBaselineChartsData(baselineId);

		const route = useRoute();
		if (Utils.isObjectWithData(route.params)) {
			baselineId.value = Number(route.params.id);
			setEditModeMessages();
			loadBaselineDetails();
			updateStats();
		}

		watch(isLoading, value => {
			if (!value) {
				nextTick(enableWatch);
			}
		})

		const { validate, validateInfos } = Validators.useAddBaselineFormRules(formState);
		const { isSubmitting, onSubmit } = useFormSubmit(validate, baselineId, formState, validateInfos, messages, resetChangedState);

		const onErrorHookRouteLeaveModal = isError => {
			if (isError) {
				message.error(t('baselines-edit.messages.error'));
			}
			modalCancel();
		};
		const {
			isSubmitting: isSubmittingRouteLeaveModal,
			onSubmit: onSubmitRouteLeaveModal,
		} = useFormSubmit(
			validate,
			baselineId,
			formState,
			validateInfos,
			messages,
			resetChangedState,
			modalOk,
			onErrorHookRouteLeaveModal,
		);

		const baselinesStore = useBaselinesStore();
		const isBrdcEnabled = computed(() => baselinesStore.settings.brdc_enabled);

		const baselineLength = computed(() => {
			const medians = allSeries.value.map(series => series.median).filter(median => median !== null);
			if (medians.length === 3) {
				return Math.sqrt(medians.reduce((acc, curr) => acc + curr * curr, 0)).toFixed(3);
			}
			return null;
		});

		return {
			baselinesStore,
			isBrdcEnabled,
			formState,
			bsOptions,
			filterOption,
			editMode,
			validateInfos,
			isLoading,
			baselineId,
			baselineDetails,
			baselineObj,
			isDeleted,
			nmeaFtpPath,
			baselineLength,
			messages,
			loadBaselineDetails,
			resetBaselineDetails,
			selectedGnssOptions,

			isStatsLoading,
			allSeries,
			allOptions,
			selectedQuality,
			qualityOptions,
			epochCounts,

			modalVisible,
			modalOk,
			modalCancel,

			isSubmitting,
			onSubmit,
			isSubmittingRouteLeaveModal,
			onSubmitRouteLeaveModal,

			isBaselineActive,
		};
	}
}
</script>

<style>
.baseline-form .ant-typography code {
	display: inline-block;
}
</style>

<style scoped>
.ui-select {
	max-width: 400px;
}
</style>
